import axios from 'axios'
import ParamsUtil from 'utils/params-util'

const API_PATH = 'api/v2/admins/sales/deals'

const SalesDealsApi = {
  /**
   * Create ::Sales::Deals
   * Invoke `POST admins/sales/deals
   * @param {Boolean} use_existing_user - whether to use existing (already created user) or add a new user from scratch
   * @param {Boolean} edit_existing_user - whether to edit existing (already created user) data
   * @param {Object} user_id - existing user id for which order will be created
   * @param {String} email - user-related field for new user (use_existing_user is false)
   * @param {String} password - user-related field for new user (use_existing_user is false)
   * @param {Date} birthdate - user-related field for new user (use_existing_user is false)
   * @param {String} name - user_detail-related field for new user (use_existing_user is false)
   * @param {String} phone - user_detail-related field for new user (use_existing_user is false)
   * @param {String} phone_code - user_detail-related field for new user (use_existing_user is false)
   * @param {String} instagram_account - user_detail-related field for new user (use_existing_user is false)
   * @param {String} lead_source - user_detail-related field for new user (use_existing_user is false)
   * @param {String} complete_address - address-related field for new user (use_existing_user is false)
   * @param {String} recipient_name - address-related field for new user (use_existing_user is false)
   * @param {String} recipient_number - address-related field for new user (use_existing_user is false)
   * @param {String} instruction - address-related field for new user (use_existing_user is false)
   * @param {Number} latitude - address-related field for new user (use_existing_user is false)
   * @param {Number} longitude - address-related field for new user (use_existing_user is false)
   * @param {Array<Object>} items - array of bought products
   * @param {Number} courier - selected shipment method with courier.rate.id field
   * @param {Object} payment_method - selected payment method {value: string, installment: nullable_int}
   * @param {String} voucher_code - selected voucher code
   * @param {String} sales_method - pre-defined option in frontend, free text in backend (warehouse sale, live auction, 24-hours auction, etc.)
   * @param {String} sales_name_in_invoice - name of sales to be displayed in the invoice
   * @param {String} deal_by - name of sales doing the dealing
   * @param {String} assist_by - name of sales doing the assisting
   * @param {String} note - free text
   * @param {Date} pickup_at - Estimated pick up date of the package
   * @param {Boolean} include_invoice - whether invoice should be included in the packaging
   * @param {Boolean} include_polaroid - whether polaroid should be included in the packaging
   * @param {Boolean} include_love_letter - whether love letter should be included in the packaging
   * @param {Number} down_payment - if not fully paid
   */
  create: async ({
    use_existing_user,
    edit_existing_user,
    user_id,
    email,
    password,
    birthdate,
    name,
    phone,
    phone_code,
    instagram_account,
    lead_source,
    complete_address,
    recipient_name,
    recipient_number,
    instruction,
    latitude,
    longitude,
    items,
    courier,
    payment_method,
    voucher_code,
    sales_method,
    sales_name_in_invoice,
    deal_by,
    assist_by,
    note,
    pickup_at,
    include_invoice,
    include_polaroid,
    include_love_letter,
    down_payment,
  }) => {
    const path = API_PATH

    const params = {
      use_existing_user,
      edit_existing_user,
      user_id,
      email,
      password,
      birthdate,
      name,
      phone,
      phone_code,
      instagram_account,
      lead_source,
      complete_address,
      recipient_name,
      recipient_number,
      instruction,
      latitude,
      longitude,

      items,
      rate_id: courier.rate.id,
      payment_method: payment_method.value,
      installment: payment_method.installment,
      voucher_code,
      sales_method,
      sales_name_in_invoice,
      deal_by,
      assist_by,
      note,
      pickup_at,
      include_invoice,
      include_polaroid,
      include_love_letter,
      down_payment,
    }

    const response = await axios.post(path, params)

    return response
  },

  /**
   * Search product list formatted specfically for the purpose of making deals
   * Invoke `POST admins/sales/deals/search_live_products
   * @param {String} query - search products by name or SKU
   */
  searchLiveProducts: async ({ query }) => {
    const path = `${API_PATH}/search_live_products?q=${query}`

    const response = await axios.get(path)

    return response
  },

  /**
   * Check whether user email is unique
   * Invoke `POST admins/sales/deals/is_user_email_unique
   * @param {String} email - full email address
   */
  isUserEmailUnique: async ({ email }) => {
    const path = `${API_PATH}/is_user_email_unique?email=${encodeURIComponent(email)}`

    const response = await axios.get(path)

    return response
  },

  /**
   * Check whether user phone is unique
   * Invoke `POST admins/sales/deals/is_user_phone_unique
   * @param {String} phone - full phone address
   */
  isUserPhoneUnique: async ({ phone }) => {
    const path = `${API_PATH}/is_user_phone_unique?full_phone=${encodeURIComponent(phone)}`

    const response = await axios.get(path)

    return response
  },

  /**
   * Get couriers list for the purpose of making deals
   * Invoke `POST admins/sales/deals/couriers
   * @param {Number} latitude - latitude of user as buyer
   * @param {Number} longitude - longitude of user as buyer
   * @param {Array<Object>} product_id_with_selling_price_list - [{product_id: int, selling_price: int}]
   */
  couriers: async ({ latitude, longitude, product_id_with_selling_price_list }) => {
    const path = `${API_PATH}/couriers`

    const params = {
      latitude,
      longitude,
      product_id_with_selling_price_list,
    }

    const response = await axios.post(path, params)

    return response
  },

  /**
   * Get all OR filtered Deals
   * Invoke `GET admins/outbound/deals
   * @param {String} q - query to search by deal or payment reference numbers
   */
  get: async (rawParams = {}) => {
    const defaults = {
      page     : 1,
      per_page : 10,
      sort_by  : 'deals.created_at',
      sort_dir : 'desc',
      scope    : 'need_action', // align with frontend default
    }

    // Merge and drop empty keys so only real filters go to the server
    const params = ParamsUtil.removeEmptyAttributes({ ...defaults, ...rawParams })

    return axios.get(API_PATH, { params })
  },

  /**
   * Get specific deals
   * Invoke `GET admins/sales/deals/:id
   * @param {Number} dealId - id of Deal
   */
  show: async ({ dealId }) => {
    const path = `${API_PATH}/${dealId}`

    const response = await axios.get(path)

    return response
  },

  /**
   * Get history list of a deal
   * Invoke `GET admins/sales/deals/:id/list_history
   * @param {Number} dealId - id of Deal
   */
  listHistory: async ({ dealId }) => {
    const path = `${API_PATH}/${dealId}/list_history`

    const response = await axios.get(path)

    return response
  },

  /**
   * Get specific history of a deal
   * Invoke `GET admins/sales/deals/get_history
   * @param {Number} historyId - id of Deal
   */
  getHistory: async ({ historyId }) => {
    const path = `${API_PATH}/get_history?history_id=${historyId}`

    const response = await axios.get(path)

    return response
  },

  /**
   * Send invoice PDF via email
   * Invoke POST admins/sales/deals/:id/send_invoice_email
   */
  sendInvoiceEmail: async ({ dealId, formData }) => {
    const path = `${API_PATH}/${dealId}/send_invoice_email`

    // IMPORTANT: axios will keep baseURL + auth automatically
    const response = await axios.post(path, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })

    return response
  },

  uploadTransferReceipt: async ({ dealId, formData }) => {
    const path = `${API_PATH}/${dealId}/upload_transfer_receipt`

    const response = await axios.put(path, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })

    return response
  },

  /**
   * Cancel specific deals
   * Invoke `GET admins/sales/deals/:id/cancel
   * @param {Number} dealId - id of Deal
   */
  cancel: async ({ dealId }) => {
    const path = `${API_PATH}/${dealId}/cancel`

    const response = await axios.post(path)

    return response
  },
}

export default SalesDealsApi
